import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check, X, Loader2 } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';

export interface Option {
  value: string;
  label: string;
  subLabel?: string | null;
}

interface SearchableSelectProps {
  options?: Option[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  labelClassName?: string;
  // Async search props
  onSearch?: (query: string) => Promise<Option[]>;
  isLoading?: boolean;
  minSearchLength?: number;
  initialOption?: Option | null;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options: staticOptions = [],
  value,
  onChange,
  label,
  placeholder = 'Seleccionar...',
  disabled = false,
  required = false,
  error,
  labelClassName = 'block text-sm font-medium text-slate-700 mb-1',
  onSearch,
  isLoading: externalLoading = false,
  minSearchLength = 2,
  initialOption = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [asyncOptions, setAsyncOptions] = useState<Option[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(initialOption);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  // Determine which options to use
  const options = onSearch ? asyncOptions : staticOptions;
  const isLoading = externalLoading || internalLoading;

  // Find selected option from current options or keep the previously selected one
  useEffect(() => {
    // If we have an override initial option matching the current value, prioritize using it
    // This allows displaying the label even if the option is not in the current (empty) search results
    if (initialOption && initialOption.value === value) {
        setSelectedOption(initialOption);
        return;
    }

    const found = options.find(opt => opt.value === value);
    if (found) {
      setSelectedOption(found);
    }
  }, [options, value, initialOption]);

  // Handle async search
  useEffect(() => {
    if (!onSearch || !isOpen) return;
    let cancelled = false;

    const performSearch = async () => {
      // If search term is too short, clear results
      if (debouncedSearchTerm.length < minSearchLength) {
        setAsyncOptions([]);
        return;
      }

      try {
        setInternalLoading(true);
        const results = await onSearch(debouncedSearchTerm);
        if (!cancelled) setAsyncOptions(results);
      } catch (err) {
        console.error('Error searching:', err);
        if (!cancelled) setAsyncOptions([]);
      } finally {
        if (!cancelled) setInternalLoading(false);
      }
    };

    performSearch();
    return () => { cancelled = true; };
  }, [debouncedSearchTerm, onSearch, isOpen, minSearchLength]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term (for static options only)
  const filteredOptions = onSearch 
    ? options 
    : options.filter(option => {
        const term = searchTerm.toLowerCase();
        const matchLabel = option.label.toLowerCase().includes(term);
        const matchSubLabel = option.subLabel && option.subLabel.toLowerCase().includes(term);
        return matchLabel || matchSubLabel;
      });

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSelectedOption(null);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (!disabled) {
      if (isOpen) {
        setIsOpen(false);
        return;
      }

      // Opening: 
      if (dropdownRef.current) {
        // Switch from hidden to visible visually
        dropdownRef.current.style.visibility = 'visible';
        dropdownRef.current.style.opacity = '1';
        // FORCE REFLOW: Critical for mobile to acknowledge visibility change before focus
        void dropdownRef.current.offsetHeight;
      }
      
      // Focus immediately - this works because element is not display:none
      inputRef.current?.focus();
      if ('ontouchstart' in window) {
        inputRef.current?.click();
      }

      setIsOpen(true);
      
      if (onSearch && !searchTerm) {
        setSearchTerm('');
      }
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className={labelClassName}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      {/* Trigger Button */}
      <div 
        onClick={handleOpen}
        className={`w-full px-3 py-2 bg-white border rounded-lg flex items-center justify-between cursor-pointer transition-colors ${
          error 
            ? 'border-red-300 focus:ring-red-500' 
            : isOpen 
              ? 'border-green-500 ring-2 ring-green-500 ring-opacity-20' 
              : 'border-slate-300 hover:border-slate-400'
        } ${disabled ? 'bg-slate-100 cursor-not-allowed opacity-75' : ''}`}
      >
        <div className="flex-1 truncate">
          {selectedOption ? (
            <div className="flex flex-col">
              <span className="font-medium text-slate-900">{selectedOption.label}</span>
              {selectedOption.subLabel && (
                <span className="text-xs text-slate-500">{selectedOption.subLabel}</span>
              )}
            </div>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-2">
          {!disabled && value && (
            <div
              onClick={clearSelection}
              className="p-1 rounded-full hover:bg-slate-100 text-slate-400"
            >
              <X size={14} />
            </div>
          )}
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown Menu - Always rendered, hidden with visibility/opacity */}
      {!disabled && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-60 flex flex-col transition-opacity duration-200"
          style={{ 
            visibility: isOpen ? 'visible' : 'hidden',
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none'
          }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                ref={inputRef}
                type="text"
                inputMode="search"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                placeholder={onSearch && minSearchLength > 0 ? `Escribe al menos ${minSearchLength} caracteres...` : "Buscar por nombre o ID..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    // Si hay opciones filtradas, seleccionar la primera
                    if (filteredOptions.length > 0) {
                      handleSelect(filteredOptions[0]);
                    }
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                autoComplete="off"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
              />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500 animate-spin" size={16} />
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1 p-1">
            {isLoading ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={16} />
                Buscando...
              </div>
            ) : onSearch && searchTerm.length < minSearchLength ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                Escribe al menos {minSearchLength} caracteres para buscar
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-sm ${
                    value === option.value
                      ? 'bg-green-50 text-green-700'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    {option.subLabel && (
                      <span className={`text-xs ${value === option.value ? 'text-green-600' : 'text-slate-500'}`}>
                        {option.subLabel}
                      </span>
                    )}
                  </div>
                  {value === option.value && <Check size={16} className="text-green-600" />}
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                No se encontraron resultados
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
